import type { MapMarker } from '@gpx-spa/geo-domain'
import { safePinColor } from './pin-marker-element.js'

export function resolveMarkerClusterColor(marker: MapMarker): string | null {
  if (marker.clusterColor !== undefined && marker.clusterColor !== '') {
    return safePinColor(marker.clusterColor)
  }
  const st = marker.style
  if (st.kind === 'pin' && st.color !== undefined) {
    return safePinColor(st.color)
  }
  if (st.kind === 'circle' && st.fillColor !== undefined) {
    return safePinColor(st.fillColor)
  }
  return null
}

export function clusterEntityKey(marker: MapMarker): string {
  const group = marker.clusterGroupId?.trim()
  return group && group.length > 0 ? group : marker.id
}

/** One marker per {@link clusterEntityKey} (e.g. one row per shipment when O/D share `clusterGroupId`). */
export function dedupeMarkersByClusterEntity(markers: Iterable<MapMarker>): MapMarker[] {
  const byKey = new Map<string, MapMarker>()
  for (const marker of markers) {
    const key = clusterEntityKey(marker)
    if (!byKey.has(key)) byKey.set(key, marker)
  }
  return [...byKey.values()]
}

export function countClusterEntities(markers: Iterable<MapMarker>): number {
  return dedupeMarkersByClusterEntity(markers).length
}

export function aggregateClusterColorCounts(
  markers: Iterable<MapMarker>
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const marker of markers) {
    const color = resolveMarkerClusterColor(marker)
    if (!color) continue
    counts.set(color, (counts.get(color) ?? 0) + 1)
  }
  return counts
}

/** Donut slices by unique {@link clusterEntityKey}, not raw marker count. */
export function aggregateClusterEntityColorCounts(
  markers: Iterable<MapMarker>
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const marker of dedupeMarkersByClusterEntity(markers)) {
    const color = resolveMarkerClusterColor(marker)
    if (!color) continue
    counts.set(color, (counts.get(color) ?? 0) + 1)
  }
  return counts
}

export function clusterColorPropertyKey(color: string): string {
  let h = 0
  for (let i = 0; i < color.length; i++) {
    h = (h * 31 + color.charCodeAt(i)) | 0
  }
  return `gpx_cc_${Math.abs(h).toString(36)}`
}
